import {
  addComponentDataFromTemplate,
  AirHandlingUnitData,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown } from "lucide-react";
import t from "@/lang/lang";
import {
  AhuComponentCatalog,
  Item,
} from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { useHtzAirHandlingUnitComponentPutMutation } from "@/services/backend/htz/ahu/api/ahu-component-api";
import { v4 } from "uuid";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

export function AddComponentDropdown({
  ahuData,
  onAhuDataChange,
  catalog,
}: {
  ahuData: AirHandlingUnitData;
  onAhuDataChange: (ahu: AirHandlingUnitData) => void;
  catalog: AhuComponentCatalog;
}) {
  const withoutGroup = catalog.items.filter((temp) => temp.group === "");
  const groups = catalog.items.reduce((prev, cur) => {
    // if without group, do nothing
    if (cur.group === "") {
      return prev;
    }

    // init group
    if (!prev.has(cur.group)) {
      prev.set(cur.group, []);
    }

    // add template
    prev.set(cur.group, [...prev.get(cur.group)!, cur]);

    return prev;
  }, new Map<string, Item[]>());
  const [put] = useHtzAirHandlingUnitComponentPutMutation();

  const add = (template: Item) => {
    const now = new Date().toISOString();
    put({
      id: v4(),
      companyId: ahuData.companyId,
      ahuId: ahuData.airHandlingUnitId,
      name: template.name,
      createdAt: now,
      updatedAt: now,
    })
      .unwrap()
      .then((component) => {
        // mutate the ahu data
        onAhuDataChange(
          addComponentDataFromTemplate(ahuData, component.id, template),
        );
      })
      .catch((error) => {
        toast(...parseRTKQueryErrorToSonner(error));
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <span>{t("Komponente hinzufügen")}</span>
          <ChevronDown className="ml-2 h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup className="grid grid-cols-3 gap-x-2">
          {withoutGroup.map((template) => (
            <DropdownMenuItem onClick={() => add(template)} key={template.id}>
              {template.name}
            </DropdownMenuItem>
          ))}
          {Array.from(groups.keys()).map((group) => (
            <DropdownMenuSub key={group}>
              <DropdownMenuSubTrigger>{group}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="grid grid-cols-2 gap-x-2">
                {groups.get(group)?.map((template) => (
                  <DropdownMenuItem
                    onClick={() => add(template)}
                    key={template.id}
                  >
                    {template.name}
                    {template.details && `, ${template.details}`}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
